import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import type { AiSpreadsheetValueType } from "@/payroll/ai-script/types";
import { CELL_FORMAT_OPTIONS } from "./cell-format-options";
import { VIEWER_OVERLAY_CLASSNAME } from "./utils";

// The right-click cell context menu: insert a row above/below, and (when the
// clicked cell is editable) re-format it. `col`/`gridRow` are 0-based grid
// coords of the clicked cell.
export interface RowInsertMenuTarget {
  x: number;
  y: number;
  gridRow: number;
  col: number;
}

export type RowInsertPosition = "above" | "below";

interface RowInsertMenuProps {
  target: RowInsertMenuTarget | null;
  // Whether the row actions (insert above/below) apply — false on a sheet the
  // user can only view, where the menu may still offer comments.
  canInsertRow?: boolean;
  onAskAi?: () => void;
  // Label for the comment action, or null to omit it.
  commentActionLabel?: string | null;
  onCommentAction?: () => void;
  // Whether the clicked cell can be re-formatted (editable + not locked + has a
  // record); the "Format cell" submenu only renders when true.
  canFormatCell: boolean;
  // The clicked cell's current value_type, for the submenu checkmark.
  currentValueType: string | null;
  // Whether the clicked row is a user-added (manual) row that can be deleted;
  // the "Delete row" item only renders when true (generated rows can't).
  canDeleteRow: boolean;
  /**
   * Label for the table-definition action, or null to omit it — defining a table
   * belongs on the selection you are pointing at, not in the toolbar, so it lives
   * here rather than as a permanent top-level control.
   */
  tableActionLabel: string | null;
  /** True while a filter or sort is on: the boundaries would be screen positions. */
  isTableActionDisabled: boolean;
  onClose: () => void;
  onInsertRow: (row: number, position: RowInsertPosition) => void;
  onFormatCell: (valueType: AiSpreadsheetValueType) => void;
  onDeleteRow: () => void;
  onTableAction: () => void;
}

export const RowInsertMenu = ({
  target,
  canInsertRow = true,
  onAskAi,
  commentActionLabel = null,
  onCommentAction,
  canFormatCell,
  currentValueType,
  canDeleteRow,
  tableActionLabel,
  isTableActionDisabled,
  onClose,
  onInsertRow,
  onFormatCell,
  onDeleteRow,
  onTableAction,
}: RowInsertMenuProps) => (
  <DropdownMenu
    open={target !== null}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
    modal={false}
  >
    <DropdownMenuTrigger asChild>
      <span
        aria-hidden
        className="pointer-events-none fixed h-0 w-0"
        style={{ left: target?.x ?? 0, top: target?.y ?? 0 }}
      />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="start"
      className={VIEWER_OVERLAY_CLASSNAME}
      // Keep the menu open when Glide refocuses its canvas after Control+click.
      onFocusOutside={(event) => event.preventDefault()}
    >
      {onAskAi && (
        <DropdownMenuItem onSelect={onAskAi}>
          Ask AI about this figure
        </DropdownMenuItem>
      )}
      {commentActionLabel !== null && (
        <>
          <DropdownMenuItem onSelect={onCommentAction}>
            {commentActionLabel}
          </DropdownMenuItem>
          {(canInsertRow || tableActionLabel !== null) && (
            <DropdownMenuSeparator />
          )}
        </>
      )}
      {tableActionLabel !== null && (
        <>
          <DropdownMenuItem
            disabled={isTableActionDisabled}
            onSelect={onTableAction}
          >
            {tableActionLabel}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      {canInsertRow && (
        <>
          <DropdownMenuItem
            disabled={!target || target.gridRow < 1}
            onSelect={() => {
              if (target) onInsertRow(target.gridRow, "above");
            }}
          >
            Insert row above
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              if (target) onInsertRow(target.gridRow, "below");
            }}
          >
            Insert row below
          </DropdownMenuItem>
        </>
      )}
      {canFormatCell && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Format cell</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={VIEWER_OVERLAY_CLASSNAME}>
            {CELL_FORMAT_OPTIONS.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={currentValueType === option.value}
                onSelect={() => onFormatCell(option.value)}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {canDeleteRow && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-50 focus:text-red-50"
            onSelect={onDeleteRow}
          >
            Delete row
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);
